import { AsyncPipe, NgIf } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewEncapsulation,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import {
  ConfigurationService,
  TimezoneService,
} from '@sc/data-access-configuration';
import {
  Dossier,
  DossierCommentsEnum,
  Fitter,
  FitterServiceType,
  getLatestDossierCommentTextByType,
  LoggerLevelType,
  NotifyAction,
  alphaNumPattern,
  emailPattern,
  findCorrespondingServiceType,
  getDeductionsFromExcessString,
  DossierComments,
  stripLegacyCommentFields,
} from '@sc/data-access-models';
import {
  TabTitleStrategyService,
  TranslatorService,
} from '@sc/data-access-translation';
import { AdaptableOverlayService } from '@sc/feature-adaptable-overlay';
import { AuthService } from '@sc/feature-auth-okta';
import {
  DossierCustomMoveInfos,
  DossierManagerService,
  ResourcesManagerService,
} from '@sc/feature-fitter-shared-libs';
import { FeatureFlippingManagerService } from '@sc/feature-flipping';
import { LoadingService } from '@sc/feature-loader';
import { SnackbarService } from '@sc/feature-snackbar';
import {
  AppointmentEndTimeValidator,
  SpinnerComponent,
} from '@sc/ui-components';
import {
  AppointmentUtils,
  dayjs,
  getPeriodReplacementVehicleUse,
} from '@sc/utils';
import { ConfigType, Dayjs } from 'dayjs';
import { BehaviorSubject, Subscription, combineLatest, noop } from 'rxjs';
import {
  AgendaFormDossier,
  PlanningFormUpdateDossierEvent,
} from '../../models';
import { DossierLightUpdateErrorInstance } from '../../models/planning-dossier-update-errors';
import {
  PlanningFormSandboxService,
  PlanningSandboxService,
} from '../../services';
import { PlanningFormAppointmentComponent } from '../planning-form-appointment';
import { PlanningFormCenterComponent } from '../planning-form-center';
import { AppointmentMobilityAddressValidator } from './appointment-addr-validator';
import { DossierInterventionValidator } from './dossier-intervention-validator';

@Component({
  selector: 'sc-planning-form',
  styleUrls: ['./planning-form.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgIf,
    ReactiveFormsModule,
    PlanningFormAppointmentComponent,
    PlanningFormCenterComponent,
    SpinnerComponent,
    AsyncPipe,
  ],
  template: ` <section class="planning-form-container">
    <form [formGroup]="form" *ngIf="(isLoading$ | async) === false">
      <sc-planning-form-appointment
        [form]="form"
        [fitters]="fitters"
        [dossier]="dossier"
        [isNotified]="isNotified"
        [touched]="touched"
        [wasFormChanged]="formWasModified"
        [errors]="errors"
        (notifyAppointmentEvent)="onNotifyAction($event)"
        (loadDossierEvent)="loadDossier($event)"
        (printDossierEvent)="onPrintDossierClick()"
      ></sc-planning-form-appointment>
      <sc-planning-center-form
        [initForm]="form"
        [fitters]="fitters"
        [dossier]="dossier"
        [isFormOpen]="isFormOpen"
        [touched]="touched"
        [connectedUserName]="connectedUserName"
        (toggleDetailsEvent)="onToggleDetailsClick($event)"
        (validateEvent)="onValidateClick($event)"
        (abandonDossierEvent)="onAbandonDossierClick()"
        (closeDetailsEvent)="onCloseClick($event)"
        (noShowClickEvent)="onNoShowClick()"
        (apmtDoneClickEvent)="onApmtDoneClick()"
        (resumeDossierEvent)="onResumeDossierClick()"
        (billingClickEvent)="onBillingClick($event)"
        (cancelClickEvent)="onCancelApmtClick($event)"
        (duplicateEvent)="onDuplicateClick()"
        (saveDraftDossierEvent)="onSaveDraftDossierClick()"
        (updateLightDossier)="onSaveLightDossier()"
        (saveChatComments)="saveChatComments.emit($event)"
      ></sc-planning-center-form>
    </form>
    <div class="loader" *ngIf="isLoading$ | async">
      <sc-spinner></sc-spinner>
    </div>
  </section>`,
})
export class PlanningFormComponent implements OnInit, OnDestroy {
  @Input() fitters: Fitter[];
  @Input() set _dossier(dossier: Dossier) {
    this.dossier = dossier;
    if (
      this.form?.get('appointment')?.get('id')?.value !==
        this.dossier?.appointment?.id ||
      this.form?.get('id')?.value !== dossier?.id
    ) {
      this.reInitForm();
    }
    this.listenFormValueChange();

    this.manageDocumentChanges(dossier);
  }
  @Input() isFormOpen: boolean;
  form: FormGroup;

  @Input() mustSaveToPrint: boolean;

  @Input() errors: DossierLightUpdateErrorInstance[];

  @Output() toggleDetailsEvent = new EventEmitter<any>();
  @Output() createEvent = new EventEmitter<Dossier>();
  @Output() bookRequestEvent = new EventEmitter<Dossier>();
  @Output() createDuplicateEvent = new EventEmitter<Dossier>();
  @Output() updateEvent = new EventEmitter<PlanningFormUpdateDossierEvent>();
  @Output() closeEvent = new EventEmitter<void>();
  @Output() saveDraft = new EventEmitter<any>();
  @Output() abandonDossier = new EventEmitter<PlanningFormUpdateDossierEvent>();
  @Output() saveLightDossier = new EventEmitter<Dossier>();
  @Output() printDossier = new EventEmitter<{
    dossier: Dossier;
    wasModified: boolean;
  }>();
  @Output() saveChatComments = new EventEmitter<DossierComments[]>();

  forceEditMode = false;
  isForcedEditModeOnOpen: boolean;
  dossier: Dossier;
  timeBeforeMoved: Dayjs;
  endTimeBeforeMoved: Dayjs;
  centerBeforeMoved: string;

  openDossierSubscription: Subscription;
  closeDossierSubscription: Subscription;

  isNotified = false;
  touched = false;
  formWasModified = false;
  initialFormRawValue;

  connectedUserName: string;
  constructor(
    private readonly configurationService: ConfigurationService,
    private readonly planningSandbox: PlanningSandboxService,
    private readonly planningFormSandbox: PlanningFormSandboxService,
    private readonly resManagerService: ResourcesManagerService,
    private readonly snackbarService: SnackbarService,
    private readonly translator: TranslatorService,
    private readonly formBuilder: FormBuilder,
    private readonly overlayDialogService: AdaptableOverlayService,
    private readonly timezoneService: TimezoneService,
    private readonly route: ActivatedRoute,
    private readonly changeDetector: ChangeDetectorRef,
    private readonly loadingService: LoadingService,
    private readonly authOktaService: AuthService,
    private readonly dossierManagerService: DossierManagerService,
    private readonly tabTitleService: TabTitleStrategyService,
    private readonly featureFlipManager: FeatureFlippingManagerService
  ) {
    this.route.queryParams.subscribe((queries) => {
      // Convert to number, then boolean --> the only values that force edit are '1', '2', '3', ...
      this.isForcedEditModeOnOpen = !!+queries.forceEdit;
    });
  }

  isLoading$ = new BehaviorSubject<boolean>(true);

  ngOnInit() {
    this.tabTitleService.setPagination({
      key: 'GlobalApplicativeConstants.tabTitles.paginations.dossierInfos',
      params: {
        dossierInfos:
          (this.dossier?.customer?.firstname ?? '') +
          ' ' +
          (this.dossier?.customer?.lastname ?? '') +
          ' ' +
          (this.dossier?.claim?.car?.licensePlateNumber ?? ''),
      },
    });

    this.form = this.getDossierForm();

    this.connectedUserName =
      this.authOktaService.getAccountFirstName() +
      ' ' +
      this.authOktaService.getAccountLastName();
    this.snackbarService.setFocusManagement(true, { form: this.form });
    if (
      this.dossier?.id &&
      (this.dossier?.appointment || this.dossier?.requestAppointment)
    ) {
      this.planningSandbox.fetchDossier(this.dossier?.id).subscribe({
        next: (dossier) => {
          if (this.dossier?.appointment) {
            this.timeBeforeMoved = dayjs(dossier.appointment.time).tz(
              this.timezoneService.getTimezone(dossier.appointment)
            );
            if (dossier.appointment.endTime) {
              this.endTimeBeforeMoved = dayjs(dossier.appointment.endTime).tz(
                this.timezoneService.getTimezone(dossier.appointment)
              );
            }
            this.centerBeforeMoved = dossier.appointment.fitterId;
          } else if (this.dossier?.requestAppointment) {
            this.timeBeforeMoved = null;
            this.endTimeBeforeMoved = null;
            this.centerBeforeMoved = dossier.requestAppointment.fitterId;
          }
          this.computeReset(
            dossier,
            this.dossier.custom as DossierCustomMoveInfos
          );
          this.initEditMode();
          if (this.dossier?.appointment)
            this.dossier.appointment.isConveying =
              this.dossier.appointment.isConveying ?? false;
          this.setDossierToForm(this.dossier);
          this.manageExternalChangesOnDossier();
          this.isLoading$.next(false);
        },
        error: (error: HttpErrorResponse) => {
          this.snackbarService.showError(
            this.translator.getInstantTranslation('Snackbar.errorTitle'),
            this.translator.getInstantTranslation(
              error?.error?.message || 'PlanningForm.fetchDossierError'
            )
          );

          console.error(error);
        },
      });

      this.planningSandbox.sendOpenDossierNotif(this.dossier.id).subscribe({
        next: noop,
        error: (error: HttpErrorResponse) => {
          this.snackbarService.showError(
            this.translator.getInstantTranslation('Snackbar.errorTitle'),
            this.translator.getInstantTranslation(
              error?.error?.message || 'PlanningForm.openDossierConcuError'
            ),
            3,
            LoggerLevelType.INFO
          );

          console.error(error);
        },
      });
    } else {
      this.initEditMode();
      this.initCreationForm();
      this.isLoading$.next(false);
    }
    this.setInterventionDurationControl();
    this.setClosingHoursControls();
    this.setReplacementVehicleControls();
    this.openDossierSubscription = this.planningSandbox
      .getOpenDossierObservable()
      .subscribe((openDossierEvent) => {
        if (this.dossier.id === openDossierEvent.dossier.id) {
          this.dossier = {
            ...this.dossier,
            currentlyInUse: openDossierEvent.dossier.currentlyInUse,
          };
          this.dossierManagerService.setDossier(this.dossier);
          this.changeDetector.markForCheck();
        }
      });
    this.closeDossierSubscription = this.planningSandbox
      .getCloseDossierObservable()
      .subscribe((closeDossierEvent) => {
        if (
          this.dossier.id === closeDossierEvent.dossier?.id &&
          this.dossier.currentlyInUse?.userId !==
            this.authOktaService.getAccountLogin()
        ) {
          // recharger le composant
          this.dossier = closeDossierEvent.dossier;
          this.setDossierToForm(this.dossier);
          this.planningSandbox
            .sendOpenDossierNotif(this.dossier.id)
            .subscribe();
        }
      });
    this.listenFormValueChange();
  }

  listenFormValueChange() {
    this.form.valueChanges.subscribe((change) => {
      this.formWasModified =
        JSON.stringify(this.initialFormRawValue) !== JSON.stringify(change) &&
        !!this.initialFormRawValue;

      this.changeDetector.markForCheck();
    });
  }

  reInitForm() {
    this.form = this.getDossierForm();
    this.snackbarService.setFocusManagement(true, { form: this.form });
    this.initEditMode();
    this.initCreationForm();
    this.listenFormValueChange();
    this.dossierManagerService.setDossier(this.dossier);
    this.changeDetector.markForCheck();
  }

  ngOnDestroy(): void {
    this.snackbarService.setFocusManagementForm(null);
    this.onQuitComponent();
  }

  @HostListener('window:beforeunload')
  onWindowUnload(): void {
    this.onQuitComponent();
  }

  onQuitComponent(): void {
    this.openDossierSubscription.unsubscribe();
    this.closeDossierSubscription.unsubscribe();
    this.dossierManagerService.onCloseDossier().subscribe();
  }

  setForceEditMode(newEditMode: boolean) {
    if (!newEditMode) {
      this.form.disable();
    } else {
      this.form.enable();
    }
    this.form.updateValueAndValidity();
  }

  manageExternalChangesOnDossier() {
    this.planningSandbox.externalChangesOnDossier$.subscribe((dossier) => {
      this.manageDocumentChanges(dossier);
    });
  }

  manageDocumentChanges(dossier: Dossier) {
    if (dossier?.id === this.form?.get('id')?.value) {
      const previousDocuments: string[] = this.form
        ?.get('claim')
        ?.value?.documents?.map((doc) => doc?.url?.split('?')[0]);
      const newDocuments: string[] = dossier?.claim?.documents?.map(
        (doc) => doc.url?.split('?')[0]
      );

      let changedDocuments = false;
      if (
        previousDocuments?.length !== newDocuments?.length &&
        !!newDocuments
      ) {
        changedDocuments = true;
      } else {
        for (let i = 0; i < previousDocuments?.length; i++) {
          if (
            previousDocuments[i] !== undefined &&
            !previousDocuments[i]?.startsWith('data:') &&
            previousDocuments[i] !== newDocuments[i]
          ) {
            changedDocuments = true;
            break;
          }
        }
      }
      const mustUpdateForm =
        changedDocuments ||
        previousDocuments?.some(
          (docUrl) => docUrl === undefined || docUrl?.startsWith('data:')
        );

      if (mustUpdateForm) {
        this.form?.get('claim').patchValue({
          documents: dossier?.claim?.documents,
        });
        this.form.updateValueAndValidity();
        if (changedDocuments) this.showExternalChangesOnDossierWarning();
      }
    }
  }

  /**
   * Takes the dossier from DB and change it with the new informations (new column, new period, ...) in order to fill the form correctly
   * @param previousDossier Dossier existing in DB
   * @param newInformations Informations that modify the dossier after event (drag / resize / ...)
   */
  computeReset(
    previousDossier: Dossier,
    newInformations: DossierCustomMoveInfos
  ) {
    if (newInformations) {
      this.dossier = this.resManagerService.generateDossierAfterMove(
        previousDossier,
        newInformations,
        this.fitters
      );
    } else {
      this.dossier = previousDossier;
    }
  }

  initEditMode() {
    this.setForceEditMode(
      this.isForcedEditModeOnOpen ||
        !this.planningFormSandbox.isReadonlyDossier(
          this.dossier,
          this.forceEditMode
        )
    );
  }

  initCreationForm() {
    const fitter = this.fitters?.find(
      (fitter) =>
        fitter?.id ===
        (this.dossier?.appointment?.fitterId ??
          this.dossier?.requestAppointment?.fitterId)
    );
    const supposedAppointment = this.dossier?.appointment?.brandId
      ? this.dossier.appointment
      : this.dossier.requestAppointment;

    const foundIntervention = findCorrespondingServiceType(
      {
        id: supposedAppointment?.interventionId,
        glassType: this.dossier?.claim?.glass?.glass,
        operationType: this.dossier?.claim?.operationType,
        isMobility: supposedAppointment?.outsideIntervention,
        replacementVehicle: supposedAppointment?.replacementVehicle,
        active: true,
        calibration: this.dossier?.claim?.calibration === 'true',
      },
      fitter?.serviceTypes || []
    );

    this.form.patchValue({
      ...this.dossier,
      id: this.dossier?.id,
      origin: 'AGENDA',
      dossierComments: this.dossier?.dossierComments || [],
      appointment: {
        ...this.dossier?.requestAppointment,
        ...this.dossier?.appointment,
        id: this.planningSandbox.isDossierDraft(this.dossier)
          ? null
          : AppointmentUtils.generateNewAppointmentId(
              fitter,
              this.dossier?.appointment?.time ?? dayjs().format()
            ),
        endTime: this.dossier?.appointment?.endTime ?? null,
        time: this.dossier?.appointment?.time,
        timezone: this.timezoneService.getFitterTimezone(fitter),
        brandId: fitter?.brandId,
        isValidatedByFitter:
          this.dossier?.appointment?.isValidatedByFitter ??
          this.dossier?.requestAppointment?.isValidatedByFitter ??
          false,
        fitterId: fitter?.id,
        interventionLocation:
          this.dossier?.appointment?.outsideIntervention ||
          this.dossier?.appointment?.isConveying
            ? this.dossier?.appointment?.interventionLocation
            : AppointmentUtils.getFitterLocation(fitter),
        outsideIntervention:
          this.dossier?.appointment?.outsideIntervention ??
          this.dossier?.requestAppointment?.outsideIntervention ??
          false,
        isReplacementVehicleSelected:
          this.dossier?.appointment?.isReplacementVehicleSelected ??
          this.dossier?.requestAppointment?.isReplacementVehicleSelected ??
          false,
        interventionDuration: this.getInterventionDuration(foundIntervention),
        interventionId: foundIntervention?.id ?? null,
      },
      claim: {
        ...this.dossier?.claim,
        car: {
          ...this.dossier?.claim?.car,
          vehicleType: this.dossier?.claim?.car.vehicleType ?? 'car',
        },
      },
    });

    this.form.updateValueAndValidity();
  }

  hasPinnedComments() {
    return (
      this.dossier?.dossierComments?.filter((comment) => comment.isPinned)
        ?.length > 0
    );
  }

  getDossierFromForm(onlyRawForm = false): Dossier {
    const formRawValue: Dossier = this.form.getRawValue();

    if (
      !!formRawValue?.policyInformation &&
      'excess' in formRawValue.policyInformation
    ) {
      const excessValue = formRawValue.policyInformation.excess as string;
      const isRepair = formRawValue?.claim?.operationType === 'Repair';
      const policyInfoResult = getDeductionsFromExcessString(
        excessValue,
        isRepair
      );

      delete formRawValue?.policyInformation?.excess;

      formRawValue.policyInformation = {
        ...formRawValue.policyInformation,
        ...policyInfoResult,
      };
    }
    return stripLegacyCommentFields({
      ...(onlyRawForm ? {} : this.dossier),
      ...formRawValue,
    });
  }

  onToggleDetailsClick($event) {
    this.toggleDetailsEvent.emit($event);
  }

  getInterventionDuration(fitterServiceType: FitterServiceType) {
    if (this.dossier?.appointment?.interventionDuration) {
      return this.dossier?.appointment?.interventionDuration;
    }

    if (fitterServiceType) {
      return this.dossier?.appointment?.outsideIntervention ??
        this.dossier?.requestAppointment?.outsideIntervention
        ? fitterServiceType.interventionDurationMobility ??
            fitterServiceType?.interventionDuration
        : fitterServiceType.interventionDuration;
    }
  }

  onAbandonDossierClick() {
    if (this.dossier?.id) {
      // Draft
      this.proceedAbandon();
    } else {
      // Dossier to Abandon
      this.overlayDialogService
        .showOverlay({ type: 'dossier-abandon-fitter', input: null })
        .subscribe((result) => {
          if (result?.type === 'success') {
            this.proceedAbandon(result.comment);
          }
        });
    }
  }

  proceedAbandon(comment?: string) {
    this.setForceEditMode(false);
    const dossier = this.getDossierFromForm();
    dossier.state = 'ABANDONED';
    if ((comment || '').trim()) {
      dossier.dossierComments = [
        ...(dossier.dossierComments || []),
        {
          userName: this?.connectedUserName,
          userType: 'FITTER',
          comment: comment?.trim(),
          timestamp: new Date().toISOString(),
          commentType: DossierCommentsEnum.abandon,
          isPinned: false,
        },
      ];
    }
    if (dossier?.id) {
      this.abandonDossier.emit({
        dossier,
        keepFormOpen: false,
        isLocal: false,
      });
    } else {
      this.createEvent.emit(dossier);
    }
  }

  //quand on appuie sur Enregistrer
  onValidateClick(isResumed: boolean) {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.touched = true;
      return;
    }
    const dossier = this.generateCreationDossier();
    const canCreate = !this.planningSandbox.isAppointmentAlreadyExist(dossier);

    if (canCreate) {
      if (
        dossier.id &&
        this.dossier?.appointment?.id &&
        !!this.dossier?.appointment?.status &&
        this.dossier?.appointment?.status !== 'CANCELLED' &&
        this.dossier?.appointment?.status !== 'NO_SHOW'
      ) {
        this.updateEvent.emit({
          dossier,
          keepFormOpen: true,
          isLocal: false,
          isResumed: isResumed,
        });
      } else if (
        dossier.id &&
        ((this.dossier?.requestAppointment && !this.dossier?.appointment) ||
          (this.dossier.appointment?.status === 'DRAFT' &&
            !this.dossier?.appointment?.id))
      ) {
        this.bookRequestEvent.emit(dossier);
      } else {
        this.createEvent.emit(dossier);
      }
      this.initialFormRawValue = this.form.getRawValue();
      this.form.markAsPristine();
      this.formWasModified = false;
      this.listenFormValueChange();
      this.mustSaveToPrint = false;
    } else {
      this.showAppointmentAlreadyExist();
    }
  }

  onDuplicateClick() {
    const dossier = this.generateCreationDossier();
    this.setForceEditMode(false);

    this.createDuplicateEvent.emit(dossier);
  }

  generateCreationDossier(): Dossier {
    const dossierForm: Dossier = this.getDossierFromForm(true);

    const fitter: Fitter = this.fitters?.find(
      (fitter) =>
        fitter?.id === this.form.get('appointment').get('fitterId')?.value
    );

    dossierForm.appointment.isValidatedByFitter = true;

    if (
      (this.dossier?.requestAppointment && !this.dossier?.appointment) ||
      dossierForm.appointment.status === 'CANCELLED' ||
      dossierForm.appointment.status === 'NO_SHOW'
    ) {
      dossierForm.appointment.id = AppointmentUtils.generateNewAppointmentId(
        fitter,
        dossierForm.appointment?.time,
        dossierForm.appointment?.endTime
      );
      dossierForm.appointment.status = 'BOOKED';
    }

    const previousAppointments = [
      ...(this.dossier?.previousAppointments || []),
    ];

    const mustPushPreviousAppointment =
      // Times or Center changed in calendar by drag&drop

      // appointment already existed
      !!this.dossier?.appointment?.id &&
      // isMovedStartAppointment :
      ((!!this.timeBeforeMoved &&
        !this.timeBeforeMoved.isSame(
          this.dossier?.appointment?.time,
          'minute'
        )) ||
        // isMovedEndAppointment :
        (!!this.endTimeBeforeMoved &&
          !!this.dossier?.appointment?.endTime &&
          !this.endTimeBeforeMoved.isSame(
            this.dossier?.appointment?.endTime,
            'minutes'
          )) ||
        // isMovedCenter :
        (!!this.centerBeforeMoved &&
          this.centerBeforeMoved !== this.dossier?.appointment?.fitterId) ||
        // Times or Center changed in form inputs

        // isChangedStartAppointment :
        !dayjs(this.dossier?.appointment?.time).isSame(
          dossierForm?.appointment?.time,
          'minutes'
        ) ||
        // isChangedEndAppointment :
        (!!this.dossier?.appointment?.endTime &&
          !dayjs(this.dossier?.appointment?.endTime).isSame(
            dossierForm?.appointment?.endTime,
            'minutes'
          )) ||
        // isChangedCenter :
        (!!this.dossier?.appointment?.fitterId &&
          this.dossier?.appointment?.fitterId !==
            dossierForm?.appointment?.fitterId));

    // Other Changes that allow 'canNotifyCustomer' to be set at 'true'
    const mustSetCanNotifyCustomer =
      mustPushPreviousAppointment ||
      // isChangedCustomerInfo :
      this.dossier?.customer?.lastname !== dossierForm?.customer?.lastname ||
      this.dossier?.customer?.firstname !== dossierForm?.customer?.firstname ||
      this.dossier?.policyInformation?.vatNumber !==
        dossierForm?.policyInformation?.vatNumber ||
      this.dossier?.customer?.phoneNumber?.countryCode !==
        dossierForm?.customer?.phoneNumber?.countryCode ||
      this.dossier?.customer?.phoneNumber?.internationalCode !==
        dossierForm?.customer?.phoneNumber?.internationalCode ||
      this.dossier?.customer?.phoneNumber?.number !==
        dossierForm?.customer?.phoneNumber?.number ||
      this.dossier?.customer?.email !== dossierForm?.customer?.email ||
      this.dossier?.customer?.location?.placeId !==
        dossierForm?.customer?.location?.placeId ||
      this.dossier?.customer?.billingLocation?.placeId !==
        dossierForm?.customer?.billingLocation?.placeId;
    // isChangedIntervention :
    this.dossier?.claim?.car?.licensePlateNumber !==
      dossierForm?.claim?.car?.licensePlateNumber ||
      this.dossier?.claim?.car?.vehicleType !==
        dossierForm?.claim?.car?.vehicleType ||
      this.dossier?.claim?.operationType !==
        dossierForm?.claim?.operationType ||
      this.dossier?.claim?.glass?.glass !== dossierForm?.claim?.glass?.glass ||
      // isChangedMobility :
      !!this.dossier?.appointment?.outsideIntervention !==
        !!dossierForm?.appointment?.outsideIntervention ||
      !!this.dossier?.appointment?.isConveying !==
        !!dossierForm?.appointment?.isConveying ||
      this.dossier?.appointment?.interventionLocation?.address !==
        dossierForm?.appointment?.interventionLocation?.address ||
      !!this.dossier?.appointment?.isReplacementVehicleSelected !==
        !!dossierForm?.appointment?.isReplacementVehicleSelected;

    if (!!this.dossier?.appointment && mustPushPreviousAppointment) {
      dossierForm.appointment.id = AppointmentUtils.generateNewAppointmentId(
        fitter,
        dossierForm.appointment?.time,
        dossierForm.appointment?.endTime
      );
      previousAppointments.push(this.dossier.appointment);
    }

    dossierForm.canNotifyCustomer =
      (!!this.dossier?.canNotifyCustomer && !this.isNotified) ||
      (!!this.dossier?.id &&
        this.dossier?.appointment?.status === 'BOOKED' &&
        mustSetCanNotifyCustomer);

    if (dossierForm.canNotifyCustomer) {
      this.isNotified = false;
    }

    if (dossierForm.appointment.assignedReplacementVehicle) {
      const apmtStart = dayjs(dossierForm.appointment.time);
      const apmtEnd = dayjs(dossierForm.appointment.endTime);

      let rvUsePeriod = getPeriodReplacementVehicleUse(dossierForm, {
        start: dossierForm.appointment.time,
        end: dossierForm.appointment.endTime,
      });

      if (rvUsePeriod.start && apmtStart.isSame(rvUsePeriod.start, 'minute')) {
        rvUsePeriod.start = null;
      }
      if (rvUsePeriod.end && apmtEnd.isSame(rvUsePeriod.end, 'minute')) {
        rvUsePeriod.end = null;
      }
      if (!rvUsePeriod.start && !rvUsePeriod.end) {
        rvUsePeriod = null;
      }

      dossierForm.appointment.assignedReplacementVehicleUse = rvUsePeriod;
    }

    return {
      ...this.dossier,
      ...dossierForm,
      appointment: {
        ...this.dossier?.appointment,
        ...dossierForm?.appointment,
      },
      previousAppointments: previousAppointments,
      claim: { ...this.dossier.claim, ...dossierForm.claim }, // dossierForm redefined the whole claim object of this.dossier so it removed sinisterComment (sinisterComment does not exist in dossierForm)
      fitterInfo: { ...this.dossier.fitterInfo, ...dossierForm.fitterInfo }, // idem for the fitterInfo.message
    };
  }

  setDossierToForm(dossier: Dossier) {
    const fitter = this.fitters?.find(
      (fitter) =>
        fitter?.id ===
        (dossier?.appointment?.fitterId ??
          dossier?.requestAppointment?.fitterId)
    );
    const supposedAppointment = this.dossier?.appointment?.brandId
      ? this.dossier.appointment
      : this.dossier.requestAppointment;
    const foundIntervention = findCorrespondingServiceType(
      {
        id: supposedAppointment?.interventionId,
        glassType: this.dossier?.claim?.glass?.glass,
        operationType: this.dossier?.claim?.operationType,
        isMobility: supposedAppointment?.outsideIntervention,
        replacementVehicle: supposedAppointment?.replacementVehicle,
        active: true,
        calibration: this.dossier?.claim?.calibration === 'true',
      },
      fitter?.serviceTypes || []
    );

    const dos = new AgendaFormDossier(
      dossier,
      this.featureFlipManager,
      this.translator
    );
    if (!dos?.appointment?.interventionDuration)
      dos.appointment.interventionDuration =
        this.getInterventionDuration(foundIntervention) ?? null;
    dos.appointment.interventionId = foundIntervention?.id ?? null;

    this.form.setValue(dos);
    this.form.updateValueAndValidity();
    this.initialFormRawValue = this.form.getRawValue();
    this.listenFormValueChange();
  }

  getDossierForm() {
    return this.formBuilder.group(
      {
        id: [null],
        dossierComments: [null],
        customer: this.formBuilder.group({
          firstname: [
            '',
            [Validators.maxLength(100), Validators.pattern(alphaNumPattern)],
          ],
          lastname: [
            '',
            [
              Validators.required,
              Validators.maxLength(100),
              Validators.pattern(alphaNumPattern),
            ],
          ],
          email: [
            '',
            [Validators.pattern(emailPattern), Validators.maxLength(100)],
          ],
          phoneNumber: this.formBuilder.group({
            number: ['', [Validators.required, Validators.maxLength(100)]],
            countryCode: [null],
            internationalCode: [null],
          }),
          location: [null],
          billingLocation: [null],
        }),
        claim: this.formBuilder.group({
          car: this.formBuilder.group({
            registrationCode: [null, []],
            licensePlateNumber: ['', [Validators.required]],
            countryCode: [null, []],
            manufacturer: [null, []],
            version: [null, []],
            model: [null, []],
            ownerShip: this.formBuilder.group({
              type: [null],
              name: [null],
            }),
            characteristics: [null],
            parts: [null],
            firstRegistration: [null],
            vehicleType: [null],
            vinNumber: [''],
          }),
          glass: this.formBuilder.group({
            glass: [null, [Validators.required]],
          }),
          sinisterDate: [null],
          operationType: [null],
          sinisterLocation: [null],
          sinisterContext: this.formBuilder.group({
            key: [null],
            value: [null],
          }),
          documents: [[]],
          calibration: [null],
          eurocode: [null],
          constructorRef: [null],
        }),
        appointment: this.formBuilder.group(
          {
            id: [null],
            time: [null, [Validators.required]],
            endTime: [null, [Validators.required]],
            fitterId: [null, [Validators.required]],
            assignedResources: [null],
            assignedInterventionVehicle: null,
            outsideIntervention: [false, [Validators.required]],
            partAvailability: ['UNAVAILABLE'],
            partProvider: [null],
            interventionLocation: [null],
            isReplacementVehicleSelected: [null],
            assignedReplacementVehicle: [null],
            assignedReplacementVehicleUse: [null],
            interventionDuration: [null],
            isValidatedByFitter: [null],
            timezone: [null],
            brandId: [null],
            isConveying: [false],
            interventionId: [null],
          },
          {
            validators: [
              AppointmentEndTimeValidator.rvConsistency(
                'assignedReplacementVehicleUse'
              ),
              AppointmentEndTimeValidator.consistency('time', 'endTime'),
              AppointmentMobilityAddressValidator.addressElementsConsistency(
                (location) => {
                  return this.planningSandbox.isMinimumAddrElementsGiven(
                    location
                  );
                }
              ),
            ],
          }
        ),
        policyInformation: this.formBuilder.group({
          insuranceChoice: [null],
          insuranceName: [null],
          insuranceCode: [null],
          policyNumber: [null],
          vatNumber: [null, [Validators.maxLength(100)]],
          excess: [null],
          sinisterNumber: [null],
        }),
      },
      {
        validators: [
          DossierInterventionValidator.interventionMobilityConsistency(() =>
            this.fitters?.find(
              (f) =>
                f.id === this.form?.get('appointment')?.get('fitterId')?.value
            )
          ),
        ],
      }
    );
  }

  onCloseClick($event) {
    this.closeEvent.emit($event);
  }

  onNoShowClick() {
    this.overlayDialogService
      .showOverlay({ input: null, type: 'no-show-comment' })
      .subscribe((result) => {
        if (result?.length && result !== 'CANCEL') {
          const noShowComment = this.translator.getInstantTranslation(
            'PlanningForm.noShowComment',
            {
              noShowDateTime: `${dayjs()
                .tz(
                  this.timezoneService.getFitterTimezone(
                    this.getFitter(this.dossier?.appointment?.fitterId)
                  )
                )
                .format('L')} ${dayjs()
                .tz(
                  this.timezoneService.getFitterTimezone(
                    this.getFitter(this.dossier?.appointment?.fitterId)
                  )
                )
                .format('LT')}`,
              username: this.connectedUserName,
              reason: result,
            }
          );

          const newDossier: Dossier = {
            ...this.dossier,
            ...this.getDossierFromForm(),
            canNotifyCustomer: true,

            appointment: {
              ...this.dossier.appointment,
              status: 'NO_SHOW',
              assignedInterventionVehicle: null,
              assignedResources: null,
              assignedReplacementVehicle: null,
            },
            dossierComments: [
              ...(this.dossier.dossierComments || []),
              {
                userName: this?.connectedUserName,
                userType: 'FITTER',
                comment: noShowComment,
                timestamp: new Date().toISOString(),
                commentType: DossierCommentsEnum.noShow,
              },
            ],
          };
          this.updateEvent.emit({
            dossier: newDossier,
            keepFormOpen: true,
            isLocal: false,
          });

          this.setForceEditMode(false);
        }
      });
  }

  getFitter(fitterId: string) {
    return this.fitters.filter((fitter) => fitter.id === fitterId)[0];
  }

  onApmtDoneClick() {
    const newDossier: Dossier = {
      ...this.getDossierFromForm(),
      canNotifyCustomer: true,
    };
    newDossier.appointment = {
      ...this.dossier.appointment,
      ...newDossier.appointment,
      status: 'COMPLETED',
    };
    this.updateEvent.emit({
      dossier: newDossier,
      keepFormOpen: true,
      isLocal: false,
    });
  }

  onResumeDossierClick() {
    this.setForceEditMode(true);

    if (this.form.enabled) {
      const newDossier = this.getDossierFromForm();
      newDossier.appointment = {
        ...this.dossier.appointment,
        ...newDossier.appointment,
        time: null,
        endTime: null,
      };
      this.updateEvent.emit({
        dossier: newDossier,
        keepFormOpen: true,
        isLocal: true,
      });
    }
  }

  onBillingClick(newBillingValue: boolean) {
    const newDossier = this.getDossierFromForm();
    newDossier.fitterInfo = {
      ...this.dossier.fitterInfo,
      ...newDossier.fitterInfo,
      isReadyToBill: newBillingValue,
    };
    this.updateEvent.emit({
      dossier: newDossier,
      keepFormOpen: true,
      isLocal: false,
    });
  }

  onCancelApmtClick(
    event: { comment: string; notifyClient: boolean } | string | void
  ) {
    this.loadingService.setLoading(true);
    let comment: string | undefined;
    let notifyClient = true;
    if (typeof event === 'object' && event !== null) {
      comment = event.comment;
      notifyClient = event.notifyClient ?? true;
    } else if (typeof event === 'string') {
      comment = event;
    }
    this.planningSandbox
      .cancelAppointment({
        dossierId: this.dossier?.id,
        comment,
        notifyClient,
      })
      .subscribe({
        next: (updatedDossier: { dossier: Dossier }) => {
          this.loadingService.setLoading(false);
          this.dossier = updatedDossier.dossier;
          this.setForceEditMode(false);
          this.changeDetector.markForCheck();
          const keepOpen =
            !this.dossier?.requestAppointment || !!this.dossier?.appointment;
          this.updateEvent.emit({
            dossier: this.dossier,
            keepFormOpen: keepOpen,
            isLocal: true,
          });
        },
        error: (error: HttpErrorResponse) => {
          this.loadingService.setLoading(false);
          this.snackbarService.showError(
            this.translator.getInstantTranslation('Snackbar.errorTitle'),
            this.translator.getInstantTranslation(
              error?.error?.message || 'PlanningForm.cancelDossierError'
            )
          );

          console.error(error);
        },
      });
  }

  setInterventionDurationControl() {
    this.form
      ?.get('claim')
      ?.get('operationType')
      ?.valueChanges.subscribe(() => {
        const partAvailability = this.form
          ?.get('appointment')
          ?.get('partAvailability')?.value;
        const glass = this.form
          ?.get('claim')
          ?.get('glass')
          ?.get('glass')?.value;
        if (partAvailability && glass) {
          this.showCheckPartAvailabilityInformation();
        }
      });

    combineLatest([
      this.form?.get('appointment')?.get('outsideIntervention')?.valueChanges,
      this.form?.get('appointment')?.get('fitterId')?.valueChanges,
      this.form?.get('appointment')?.get('interventionId')?.valueChanges,
    ]).subscribe(([isOutside, currentFitterId, interventionId]) => {
      const currentFitter = this.fitters.find((f) => f.id === currentFitterId);

      if (currentFitter) {
        const currentIntervention = currentFitter.serviceTypes?.find(
          (i) => i.id === interventionId
        );

        if (currentIntervention) {
          const startTime = this.form?.get('appointment')?.get('time')?.value;
          const endTime = this.form?.get('appointment')?.get('endTime')?.value;

          const currentDiff = dayjs(endTime || startTime).diff(
            startTime,
            'minutes'
          );
          const previousIntervention = currentFitter.serviceTypes?.find(
            (i) => i.id === this.dossier?.appointment?.interventionId
          );
          const previousDuration =
            (this.dossier?.appointment?.outsideIntervention
              ? previousIntervention?.interventionDurationMobility ||
                previousIntervention?.interventionDuration
              : previousIntervention?.interventionDuration) ?? 60;

          if (currentDiff === previousDuration) {
            const usedDuration =
              (isOutside
                ? currentIntervention.interventionDurationMobility
                : currentIntervention.interventionDuration) ||
              currentIntervention.interventionDuration;

            const newEndTime = dayjs(startTime).add(usedDuration, 'minutes');

            if (
              newEndTime.isValid() &&
              !dayjs(endTime).isSame(newEndTime, 'minute')
            ) {
              this.form
                ?.get('appointment')
                ?.get('endTime')
                ?.setValue(newEndTime.format());
            }
          }
        }
      }
    });
  }

  setClosingHoursControls() {
    const startTime = this.form?.get('appointment')?.get('time');
    const newEndTime = this.form?.get('appointment')?.get('endTime');

    this.form
      ?.get('appointment')
      ?.get('endTime')
      ?.valueChanges.subscribe(() => {
        const fitterId = this.form?.get('appointment')?.get('fitterId')?.value;
        const isOutsideIntervention = this.form
          ?.get('appointment')
          ?.get('outsideIntervention')?.value;

        if (
          startTime.valid &&
          startTime?.value &&
          newEndTime.valid &&
          newEndTime?.value &&
          this.isAppointmentAfterFitterOpenings(
            startTime.value,
            newEndTime.value,
            fitterId,
            isOutsideIntervention
          )
        ) {
          this.showAppointmentNotInOpeningsWarning();
        }
      });

    this.form
      ?.get('appointment')
      ?.get('time')
      ?.valueChanges.subscribe((newStartTime) => {
        const fitterId = this.form?.get('appointment')?.get('fitterId')?.value;
        const isOutsideIntervention = this.form
          ?.get('appointment')
          ?.get('outsideIntervention')?.value;

        if (
          newStartTime &&
          this.isAppointmentBeforeFitterOpenings(
            newStartTime,
            fitterId,
            isOutsideIntervention
          )
        ) {
          this.showAppointmentNotInOpeningsWarning();
        }
      });

    this.form
      ?.get('appointment')
      ?.get('fitterId')
      ?.valueChanges.subscribe((newFitterId) => {
        const startTime = this.form?.get('appointment')?.get('time')?.value;
        const endTime = this.form?.get('appointment')?.get('endTime')?.value;
        const isOutsideIntervention = this.form
          ?.get('appointment')
          ?.get('outsideIntervention')?.value;

        if (
          startTime &&
          endTime &&
          (this.isAppointmentBeforeFitterOpenings(
            startTime,
            newFitterId,
            isOutsideIntervention
          ) ||
            this.isAppointmentAfterFitterOpenings(
              startTime,
              endTime,
              newFitterId,
              isOutsideIntervention
            ))
        ) {
          this.showAppointmentNotInOpeningsWarning();
        }
      });

    this.form
      ?.get('appointment')
      ?.get('outsideIntervention')
      ?.valueChanges.subscribe((newOutsideIntervention) => {
        const startTime = this.form?.get('appointment')?.get('time')?.value;
        const endTime = this.form?.get('appointment')?.get('endTime')?.value;
        const fitterId = this.form?.get('appointment')?.get('fitterId')?.value;

        if (
          startTime &&
          endTime &&
          (this.isAppointmentBeforeFitterOpenings(
            startTime,
            fitterId,
            newOutsideIntervention
          ) ||
            this.isAppointmentAfterFitterOpenings(
              startTime,
              endTime,
              fitterId,
              newOutsideIntervention
            ))
        ) {
          this.showAppointmentNotInOpeningsWarning();
        }
      });
  }

  setReplacementVehicleControls() {
    this.form
      ?.get('appointment')
      ?.get('endTime')
      ?.valueChanges?.subscribe(() => {
        this.checkWarnRVUsePeriod();
      });
    this.form
      ?.get('appointment')
      ?.get('time')
      ?.valueChanges?.subscribe(() => {
        this.checkWarnRVUsePeriod();
      });
  }

  checkWarnRVUsePeriod() {
    const apmtStart = this.form?.get('appointment')?.get('time').value;
    const apmtEnd =
      this.form?.get('appointment')?.get('endTime').value || apmtStart;

    const apmtStartMoment = dayjs(apmtStart);
    const apmtEndMoment = dayjs(apmtEnd);

    const currentRVPeriod = this.form
      ?.get('appointment')
      ?.get('assignedReplacementVehicleUse').value;

    if (
      currentRVPeriod?.start &&
      currentRVPeriod?.end &&
      !apmtStartMoment.isBetween(
        currentRVPeriod?.start,
        currentRVPeriod?.end,
        'minutes',
        '[]'
      ) &&
      !apmtEndMoment.isBetween(
        currentRVPeriod?.start,
        currentRVPeriod?.end,
        'minutes',
        '[]'
      )
    ) {
      this.showReplacementVehiclePeriodWarn();
    }
  }

  getExtremeOpeningHours(
    refTime: ConfigType,
    fitter: Fitter,
    isOutsideIntervention: boolean
  ): { startTime: Dayjs; endTime: Dayjs } {
    const refMoment = dayjs(refTime).clone();

    let openingHours = isOutsideIntervention
      ? fitter.openingHours?.outside
      : fitter.openingHours?.center;

    if (!openingHours?.length) {
      openingHours = fitter.openingHours?.center;
    }

    if (
      !openingHours.filter(
        (openingHour) => openingHour.open.day === refMoment.isoWeekday()
      ).length
    ) {
      return null;
    }

    let minStartTime = refMoment.clone().endOf('day');
    let maxEndTime = refMoment.clone().startOf('day');
    openingHours.forEach((openingHour) => {
      if (openingHour.open.day !== refMoment.isoWeekday()) {
        return;
      }

      const openHour = openingHour.open.time.slice(0, 2);
      const openMin = openingHour.open.time.slice(2, 4);
      const openingStart = refMoment
        .clone()
        .hour(+openHour)
        .minute(+openMin);

      const closeHour = openingHour.close.time.slice(0, 2);
      const closeMin = openingHour.close.time.slice(2, 4);
      const openingEnd = refMoment
        .clone()
        .hour(+closeHour)
        .minute(+closeMin);

      if (openingStart.isBefore(minStartTime)) {
        minStartTime = minStartTime
          .hour(openingStart.hour())
          .minute(openingStart.minute());
      }
      if (openingEnd.isAfter(maxEndTime)) {
        maxEndTime = maxEndTime
          .hour(openingEnd.hour())
          .minute(openingEnd.minute());
      }
    });

    return {
      startTime: minStartTime,
      endTime: maxEndTime,
    };
  }

  isAppointmentBeforeFitterOpenings(
    startTime: ConfigType,
    fitterId: string,
    isOutsideIntervention: boolean
  ) {
    const fitter = this.fitters.find((f) => f.id === fitterId);
    if (!fitter) {
      return false;
    }

    const extremeOpenings = this.getExtremeOpeningHours(
      startTime,
      fitter,
      isOutsideIntervention
    );

    if (!extremeOpenings) {
      return true;
    }

    return !extremeOpenings.startTime.isSameOrBefore(startTime, 'minute');
  }

  isAppointmentAfterFitterOpenings(
    startTime: ConfigType,
    endTime: ConfigType,
    fitterId: string,
    isOutsideIntervention: boolean
  ) {
    const fitter = this.fitters.find((f) => f.id === fitterId);
    if (!fitter) {
      return false;
    }

    const extremeOpenings = this.getExtremeOpeningHours(
      startTime,
      fitter,
      isOutsideIntervention
    );

    if (!extremeOpenings) {
      return true;
    }

    return !extremeOpenings.endTime.isSameOrAfter(endTime, 'minute');
  }

  showExternalChangesOnDossierWarning() {
    this.snackbarService.showBottomSnackBar(
      this.translator.getInstantTranslation(
        'PlanningForm.externalChangesOnDossierWarning.title'
      ),
      this.translator.getInstantTranslation(
        'PlanningForm.externalChangesOnDossierWarning.text'
      ),
      5,
      LoggerLevelType.WARNING
    );
  }

  showAppointmentNotInOpeningsWarning() {
    this.snackbarService.showBottomSnackBar(
      this.translator.getInstantTranslation(
        'PlanningForm.appointmentNotInOpeningsWarning.title'
      ),
      this.translator.getInstantTranslation(
        'PlanningForm.appointmentNotInOpeningsWarning.text',
        {
          outsideIntervention: +this.form
            .get('appointment')
            ?.get('outsideIntervention').value,
        }
      ),
      5,
      LoggerLevelType.WARNING
    );
  }

  showCheckPartAvailabilityInformation() {
    this.snackbarService.showBottomSnackBar(
      this.translator.getInstantTranslation(
        'PlanningForm.partAvailabilityInformation.title'
      ),
      this.translator.getInstantTranslation(
        'PlanningForm.partAvailabilityInformation.text'
      ),
      5,
      LoggerLevelType.INFO
    );
  }

  showAppointmentAlreadyExist() {
    this.snackbarService.showError(
      this.translator.getInstantTranslation(
        'PlanningForm.appointmentAlreadyExist.title'
      ),
      this.translator.getInstantTranslation(
        'PlanningForm.appointmentAlreadyExist.text'
      ),
      5
    );
  }

  showReplacementVehiclePeriodWarn() {
    this.snackbarService.showBottomSnackBar(
      this.translator.getInstantTranslation(
        'PlanningForm.replacementVehiclePeriodWarn.title'
      ),
      this.translator.getInstantTranslation(
        'PlanningForm.replacementVehiclePeriodWarn.text'
      ),
      5,
      LoggerLevelType.WARNING
    );
  }

  onNotifyAction(type: NotifyAction) {
    this.loadingService.setLoading(true);
    this.isNotified = true;
    this.planningSandbox
      .notifyAppointment({
        dossierId: this.dossier?.id,
        notifType: type,
      })
      .subscribe(
        () => {
          this.loadingService.setLoading(false);
          this.snackbarService.showSuccess(
            this.translator.getInstantTranslation(
              'PlanningForm.sendNotifSuccess'
            )
          );
        },
        (error: HttpErrorResponse) => {
          this.loadingService.setLoading(false);
          this.snackbarService.showError(
            this.translator.getInstantTranslation('Snackbar.errorTitle'),
            this.translator.getInstantTranslation(
              error?.error?.message || 'PlanningForm.sendNotifError'
            )
          );

          console.error(error);
        }
      );
  }

  loadDossier(dossier: Dossier) {
    const appointment = this.form?.get('appointment');
    const time = appointment?.get('time')?.value;
    const endTime = appointment?.get('endTime')?.value;

    this.dossier.customer = dossier.customer;
    this.dossier.policyInformation = dossier.policyInformation;
    this.dossier.searchCriteria = dossier.searchCriteria;
    this.dossier.claim = {
      car: dossier.claim?.car,
      operationType: null,
      glass: null,
    };
    this.dossier.appointment = {
      id: null,
      time: time,
      endTime: endTime,
      timezone:
        dossier.appointment?.timezone || dossier.requestAppointment?.timezone,
      fitterId:
        dossier.appointment?.fitterId || dossier.requestAppointment?.fitterId,
      brandId:
        dossier.appointment?.brandId || dossier.requestAppointment?.brandId,
      interventionLocation:
        dossier.appointment?.interventionLocation ||
        dossier.requestAppointment?.interventionLocation,
      outsideIntervention:
        dossier.appointment?.outsideIntervention ||
        dossier.requestAppointment?.outsideIntervention,
      comment: getLatestDossierCommentTextByType(dossier, [
        DossierCommentsEnum.appointment,
      ]),
    };

    this.reInitForm();

    appointment?.get('time')?.setValue(time);
    appointment?.get('endTime')?.setValue(endTime);
  }

  onSaveDraftDossierClick() {
    const dossier = {
      ...this.getDossierFromForm(),
      insurer: { code: this.configurationService?.getConfig()?.id },
    };

    this.formWasModified = false;
    this.saveDraft.emit(dossier);
  }

  onPrintDossierClick() {
    const dossierToPrint = {
      ...this.getDossierFromForm(),
      state: this.dossier?.state,
      appointment: {
        ...this.getDossierFromForm()?.appointment,
        status: this.dossier?.appointment?.status,
        isValidatedByFitter: this.dossier?.appointment?.isValidatedByFitter,
      },
    } as Dossier;
    this.printDossier.emit({
      dossier: dossierToPrint,
      wasModified: this.formWasModified,
    });

    this.formWasModified = false;
  }

  onSaveLightDossier() {
    const dossier = this.getDossierFromForm();

    this.formWasModified = false;
    this.saveLightDossier.emit({ ...this.dossier, ...dossier } as Dossier);
  }
}
